import Sidebar from "@components/sidebar/Sidebar";
import BuilderTopbar from "@components/topbar/BuilderTopbar";
import BuilderMain from "@components/main/BuilderMain";
import BuilderActions from "@components/builderActions/BuilderActions";
import BuilderActionsTabs from "@components/builderActions/BuilderActionsTabs";
import BuilderMainTabs from "@components/main/BuilderMainTabs";

import { motion } from "framer-motion";
import { Outlet, useLocation } from "react-router-dom";
import { useAppNavigate, useAppSelector, usePermissions, useAppDispatch } from "@app/hooks";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { Actions } from "@typedefs/interfaces";
import { useEffect, useState, useRef } from "react";
import { useHandleProjectState } from "./useHandleProjectState";
import { useProjectAreaStatus } from "./useProjectAreaStatus";
import { encodeProjectId } from "@utils/projectIdEncoder";
import { useSynchronizeMapData } from "./useSynchronizeMapData";
import { useProjectCopyBlock } from "@app/hooks/useProjectCopyBlock";
import ProjectCopyBlockModal from "@components/projectCopyBlockModal";
import { apiSlice } from "@app/apiSlice";

const ProjectContainer = () => {
	const navigate = useAppNavigate();
	const location = useLocation();

	const { project, projectId, isLoading } = useHandleProjectState();
	useProjectAreaStatus();
	useSynchronizeMapData();
	const { isPublic } = usePermissions();
	const [isInResults, setIsInResults] = useState(false);

	const {
		activeAction: activeTabId,
		tabsOpen,
		projectList,
	} = useAppSelector(selectCurrentBuilder);

	const projectIdNum = projectId ? parseInt(projectId, 10) : undefined;
	const { isBlocked, job, progress, status } = useProjectCopyBlock(projectIdNum);
	const prevJobStatusRef = useRef(status);
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (prevJobStatusRef.current === ('pending' as const) && status === 'completed') {
			dispatch(
				apiSlice.util.invalidateTags([
					{ type: 'DetailedProjects', id: projectIdNum },
					{ type: 'FastProjects', id: projectIdNum }
				])
			);
		}
		prevJobStatusRef.current = status;
	}, [status, dispatch, projectIdNum]);

	const handleReturnToList = () => {
		navigate({ path: '/select-project' });
	};

	const actionsGridClass = !tabsOpen
		? "actions-close"
		: [
			Actions.Comments,
			Actions.Help,
			Actions.Changes,
			Actions.Definitions,
		].includes(activeTabId)
			? "actions-small"
			: "";

	useEffect(() => {
		if (location) {
			const pathSegments = location.pathname.split("/");
			const projectSegmentIndex = isPublic ? 2 : 1;
			const projectIdIndex = isPublic ? 3 : 2;
			const subpageIndex = isPublic ? 4 : 3;

			// Check if we're in a project route but missing the subpage
			if (pathSegments[projectSegmentIndex] === "project" &&
				(!pathSegments[subpageIndex] || pathSegments[subpageIndex] === "")) {

				// Handle navigation based on project ID
				if (pathSegments[projectIdIndex] === "new") {
					// For new projects, redirect to description
					const redirectPath = isPublic
						? "/auth/login"
						: "/project/new/description";
					navigate({ path: redirectPath, options: { replace: true }, ignorePublic: true });
				} else {
					const encodedId = projectId ? encodeProjectId(Number(projectId)) : undefined;
					navigate({ path: `/project/${isPublic ? encodedId : projectId}/activities`, options: { replace: true } });
				}
			}

			// Set if we're in results view
			setIsInResults(
				pathSegments[projectSegmentIndex] === "project" &&
				pathSegments[subpageIndex] === "results"
			);
		}
	}, [location, navigate, projectId]);

	return (
		<motion.div className={`builder-container ${actionsGridClass}`}>
			<Sidebar />
			<BuilderTopbar
				projectLabel={project ? project.name : "New Project"}
				isLoading={isLoading}
				stepsList={projectList}
			/>
			<BuilderMain className={isInResults ? "" : "bt-1-common-dark"}>
				{!isBlocked && <Outlet />}
			</BuilderMain>
			{
				isInResults ?
					<BuilderMainTabs isInResults />
					: null
			}
			<BuilderActionsTabs />
			<BuilderActions />
			<ProjectCopyBlockModal
				isOpen={isBlocked}
				progress={progress}
				status={status}
				errorMessage={job?.error_message}
				onReturnToList={handleReturnToList}
			/>
		</motion.div>
	);
};

export default ProjectContainer;
